import { Box } from "../../box";
import { Children, forwardRef, useMemo } from "react";
import { ClearFieldContext, useFieldInputProps } from "../../field";
import { ClearToolbar, useToolbarProps } from "../../toolbar";
import { InputGroupContext } from "./InputGroupContext";
import { cssModule, getSlotKey, isNil, mergeProps, omitProps, resolveChildren, useHasChild, useMergedRefs } from "../../shared";
import { useResponsiveValue } from "../../styling";
import { TextAddon } from "./TextAddon";
import { jsx as _jsx } from "react/jsx-runtime";
const DefaultElement = "div";

function toAddon(element, key) {
  if (getSlotKey(element) === "text") {
    return /*#__PURE__*/_jsx(TextAddon, {
      children: element
    }, key);
  }

  return element;
}

export function InnerInputGroup({
  as = DefaultElement,
  children,
  disabled,
  fluid,
  forwardedRef,
  readOnly,
  ...rest
}) {
  const fluidValue = useResponsiveValue(fluid);
  const [toolbarProps] = useToolbarProps();
  const [fieldProps] = useFieldInputProps();
  const inputProps = mergeProps({
    disabled,
    fluid: fluidValue,
    readOnly
  }, omitProps(toolbarProps, ["orientation"]), fieldProps);
  const ref = useMergedRefs(forwardedRef);
  const transformedChildren = useMemo(() => {
    const elements = Children.toArray(resolveChildren(children)).filter(x => !isNil(x));

    if (elements.length <= 1) {
      throw new Error("An input group component must have 2 or 3 children.");
    }

    return Children.toArray(children).reduce((acc, x, index) => {
      acc.push(toAddon(x, index));
      return acc;
    }, []);
  }, [children]);
  const hasTextInput = useHasChild(".o-ui-input-group-text-input", ref);
  return /*#__PURE__*/_jsx(Box, { ...mergeProps(rest, {
      as,
      className: cssModule("o-ui-input-group", fluidValue && "fluid", hasTextInput && "has-text-input"),
      ref
    }),
    children: /*#__PURE__*/_jsx(ClearToolbar, {
      children: /*#__PURE__*/_jsx(ClearFieldContext, {
        children: /*#__PURE__*/_jsx(InputGroupContext.Provider, {
          value: inputProps,
          children: transformedChildren
        })
      })
    })
  });
}
InnerInputGroup.defaultElement = DefaultElement;
export const InputGroup = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerInputGroup, { ...props,
  forwardedRef: ref
}));